import { supabase } from './supabase';
import { ProjectService } from './projects';
import { CreativeService } from './creatives';
import { RecentAnalysesService } from './recent-analyses';

export interface SearchResult {
  id: string;
  type: 'project' | 'creative' | 'analysis';
  title: string;
  description?: string;
  url: string;
  thumbnail?: string;
  score?: number;
  projectName?: string;
  createdAt: string;
  updatedAt: string;
  metadata?: {
    [key: string]: any;
  };
}

export interface SearchOptions {
  query: string;
  types?: ('project' | 'creative' | 'analysis')[];
  limit?: number;
  offset?: number;
  projectId?: string;
}

export interface SearchResponse {
  results: SearchResult[];
  totalCount: number;
  hasMore: boolean;
}

export class GlobalSearchService {
  private static readonly DEFAULT_LIMIT = 20;
  private static readonly MAX_LIMIT = 100;

  /**
   * Perform global search across all content types
   */
  static async search(options: SearchOptions): Promise<SearchResponse> {
    const { query, types = ['project', 'analysis'], limit = this.DEFAULT_LIMIT, offset = 0 } = options;

    if (!query || query.trim().length < 2) {
      return {
        results: [],
        totalCount: 0,
        hasMore: false
      };
    }

    const trimmedQuery = query.trim();
    const searchLimit = Math.min(limit, this.MAX_LIMIT);
    
    try {
      const searchPromises: Promise<SearchResult[]>[] = [];
      
      // Search projects
      if (types.includes('project')) {
        searchPromises.push(this.searchProjects(trimmedQuery, searchLimit));
      }
      
      // Search creatives
      if (types.includes('creative')) {
        searchPromises.push(this.searchCreatives(trimmedQuery, options.projectId, searchLimit));
      }
      
      // Search analyses
      if (types.includes('analysis')) {
        searchPromises.push(this.searchAnalyses(trimmedQuery, searchLimit));
      }

      const searchResults = await Promise.all(searchPromises);
      const allResults = searchResults.flat();
      
      // Sort results by relevance and date
      const sortedResults = this.sortSearchResults(allResults, trimmedQuery);
      
      // Apply pagination
      const paginatedResults = sortedResults.slice(offset, offset + searchLimit);
      
      return {
        results: paginatedResults,
        totalCount: allResults.length,
        hasMore: allResults.length > offset + searchLimit
      };
    } catch (error) {
      console.error('Global search error:', error);
      throw error;
    }
  }

  /**
   * Search projects and convert to SearchResult format
   */
  private static async searchProjects(query: string, limit: number): Promise<SearchResult[]> {
    try {
      const projects = await ProjectService.searchProjects(query, limit);
      
      return projects.map(project => ({
        id: project.id,
        type: 'project' as const,
        title: project.name,
        description: project.description || undefined,
        url: `/dashboard/projects/${project.id}`,
        createdAt: project.created_at || new Date().toISOString(),
        updatedAt: project.updated_at || new Date().toISOString(),
        metadata: {
          memberCount: project.memberCount,
          owner: project.owner?.name
        }
      }));
    } catch (error) {
      console.error('Project search error:', error);
      return [];
    }
  }

  /**
   * Search creatives and convert to SearchResult format
   */
  private static async searchCreatives(query: string, projectId?: string, limit: number = 20): Promise<SearchResult[]> {
    try {
      const creatives = await CreativeService.searchCreatives(query, projectId, limit);
      
      return creatives.map(creative => ({
        id: creative.id,
        type: 'creative' as const,
        title: creative.name,
        description: `Creative in ${creative.projectName || 'Unassigned'}`,
        url: creative.project_id 
          ? `/dashboard/projects/${creative.project_id}?creative=${creative.id}`
          : `/dashboard/analysis?creativeId=${creative.id}`,
        thumbnail: creative.url || undefined,
        createdAt: creative.created_at || new Date().toISOString(),
        updatedAt: creative.updated_at || new Date().toISOString(),
        projectName: creative.projectName,
        score: creative.analysis?.overallScore,
        metadata: {
          fileType: creative.file_type,
          fileSize: creative.file_size,
          hasAnalysis: !!creative.analysis
        }
      }));
    } catch (error) {
      console.error('Creative search error:', error);
      return [];
    }
  }

  /**
   * Search analyses and convert to SearchResult format
   */
  private static async searchAnalyses(query: string, limit: number): Promise<SearchResult[]> {
    try {
      const analyses = await RecentAnalysesService.getFilteredAnalyses({
        searchTerm: query,
        limit
      });
      
      return analyses.map(analysis => {
        const platformsAnalyzed = analysis.platforms ? Object.keys(analysis.platforms).length : 0;
        const mainIssues = analysis.mainIssues || [];
        
        return {
          id: analysis.id,
          type: 'analysis' as const,
          title: analysis.creativeName,
          description: `Analysis • Score: ${analysis.overallScore}% • ${platformsAnalyzed} platforms`,
          url: `/dashboard/analysis?id=${analysis.id}`,
          thumbnail: analysis.thumbnail || undefined,
          createdAt: analysis.analysisDate,
          updatedAt: analysis.analysisDate,
          score: analysis.overallScore,
          projectName: analysis.project,
          metadata: {
            platformsAnalyzed: platformsAnalyzed,
            complianceIssues: mainIssues.length,
            performanceIssues: mainIssues.length
          }
        };
      });
    } catch (error) {
      console.error('Analysis search error:', error);
      return [];
    }
  }

  /**
   * Sort search results by relevance
   */
  private static sortSearchResults(results: SearchResult[], query: string): SearchResult[] {
    const queryLower = query.toLowerCase();
    
    return results.sort((a, b) => {
      // Calculate relevance score
      const aScore = this.calculateRelevanceScore(a, queryLower);
      const bScore = this.calculateRelevanceScore(b, queryLower);
      
      // Sort by relevance first, then by date
      if (aScore !== bScore) {
        return bScore - aScore;
      }
      
      // Handle potential undefined dates
      const aDate = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const bDate = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      
      return bDate - aDate;
    });
  }

  /**
   * Calculate relevance score for search result
   */
  private static calculateRelevanceScore(result: SearchResult, query: string): number {
    let score = 0;
    const titleLower = (result.title || '').toLowerCase();
    const descriptionLower = (result.description || '').toLowerCase();
    
    // Exact title match gets highest score
    if (titleLower === query) {
      score += 100;
    }
    // Title starts with query
    else if (titleLower.startsWith(query)) {
      score += 80;
    }
    // Title contains query
    else if (titleLower.includes(query)) {
      score += 60;
    }
    
    // Description matches
    if (descriptionLower.includes(query)) {
      score += 30;
    }
    
    // Project name matches (for creatives and analyses)
    if (result.projectName && result.projectName.toLowerCase().includes(query)) {
      score += 40;
    }
    
    // Boost recent items
    if (result.updatedAt) {
      const daysSinceUpdate = (Date.now() - new Date(result.updatedAt).getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceUpdate < 7) {
        score += 10;
      }
    }
    
    // Boost items with good analysis scores
    if (result.score && result.score >= 80) {
      score += 5;
    }
    
    return score;
  }

  /**
   * Get search suggestions based on recent activity
   */
  static async getSearchSuggestions(query: string = '', limit: number = 5): Promise<string[]> {
    if (query.length < 2) {
      return [];
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      // Get recent project names
      const { data: projects } = await supabase
        .from('projects')
        .select('name')
        .eq('user_id', user.id)
        .ilike('name', `%${query}%`)
        .order('updated_at', { ascending: false })
        .limit(limit);

      // Get recent creative names
      const { data: creatives } = await supabase
        .from('creatives')
        .select('name')
        .ilike('name', `%${query}%`)
        .order('created_at', { ascending: false })
        .limit(limit);

      const suggestions = [
        ...(projects || []).map(p => p.name),
        ...(creatives || []).map(c => c.name)
      ];

      // Remove duplicates and limit
      return [...new Set(suggestions)].slice(0, limit);
    } catch (error) {
      console.error('Search suggestions error:', error);
      return [];
    }
  }
}